// src/pages/staff/components/StaffCreateModal.tsx

import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Alert,
  Space,
  Tooltip,
  InputNumber,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  PhoneOutlined,
  BankOutlined,
  TeamOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { CreateStaffPayload } from '../../../types/api.types';
import { STAFF_ROLE_OPTIONS, DEPARTMENT_OPTIONS } from '../../../types/api.types';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface StaffCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateStaffPayload) => void;
  loading: boolean;
}

const StaffCreateModal: React.FC<StaffCreateModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Nettoyer les valeurs
      const payload: CreateStaffPayload = {
        email: values.email.trim(),
        password: values.password,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        phone: values.phone?.trim() || undefined,
        role: values.role,
        department: values.department?.trim() || undefined,
        position: values.position?.trim() || undefined,
        salary: values.salary || undefined,
        notes: values.notes?.trim() || undefined,
      };

      onSubmit(payload);
    } catch (error) {
      console.error('Erreur de validation du formulaire:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    form.setFieldsValue({ password });
  };

  return (
    <Modal
      title={
        <Space>
          <UserOutlined style={{ color: '#52c41a' }} />
          <span>Ajouter un nouveau membre du personnel</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      okText="Créer le personnel"
      cancelText="Annuler"
      destroyOnClose
      styles={{
        body: { padding: '24px' },
      }}
    >
      <Alert
        message="Information importante"
        description="Le nouveau membre du personnel recevra ses identifiants de connexion par email et devra changer son mot de passe lors de sa première connexion."
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        autoComplete="off"
      >
        {/* Informations personnelles */}
        <Title level={4} style={{ marginBottom: '16px', color: '#1a1a1a' }}>
          Informations personnelles
        </Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstName"
              label="Prénom"
              rules={[
                { required: true, message: 'Le prénom est requis' },
                { min: 2, message: 'Le prénom doit contenir au moins 2 caractères' },
                { max: 50, message: 'Le prénom ne peut pas dépasser 50 caractères' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Entrez le prénom"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastName"
              label="Nom de famille"
              rules={[
                { required: true, message: 'Le nom de famille est requis' },
                { min: 2, message: 'Le nom doit contenir au moins 2 caractères' },
                { max: 50, message: 'Le nom ne peut pas dépasser 50 caractères' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Entrez le nom de famille"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Adresse email"
              rules={[
                { required: true, message: 'L\'email est requis' },
                { type: 'email', message: 'Format d\'email invalide' },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="nom@exemple.com"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label="Numéro de téléphone"
              rules={[
                { pattern: /^\+?[\d\s-()]+$/, message: 'Format de téléphone invalide' },
              ]}
            >
              <Input
                prefix={<PhoneOutlined />}
                placeholder="226 XX XX XX XX"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="password"
          label={
            <Space>
              <span>Mot de passe temporaire</span>
              <Tooltip title="Générer un mot de passe sécurisé automatiquement">
                <InfoCircleOutlined 
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={generatePassword}
                />
              </Tooltip>
            </Space>
          }
          rules={[
            { required: true, message: 'Le mot de passe est requis' },
            { min: 8, message: 'Le mot de passe doit contenir au moins 8 caractères' },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
              message: 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre',
            },
          ]}
          extra={
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Cliquez sur l'icône d'information pour générer un mot de passe automatiquement
            </Text>
          }
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Mot de passe temporaire"
            size="large"
          />
        </Form.Item>

        {/* Informations professionnelles */}
        <Title level={4} style={{ marginBottom: '16px', marginTop: '24px', color: '#1a1a1a' }}>
          Informations professionnelles
        </Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="role"
              label="Rôle"
              rules={[{ required: true, message: 'Le rôle est requis' }]}
            >
              <Select
                placeholder="Sélectionnez un rôle"
                size="large"
                optionRender={(option) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{option.data.label}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {option.data.description}
                    </div>
                  </div>
                )}
              >
                {STAFF_ROLE_OPTIONS.map(role => (
                  <Option 
                    key={role.value} 
                    value={role.value}
                    label={role.label}
                    description={role.description}
                  >
                    {role.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="department"
              label="Département"
            >
              <Select
                placeholder="Sélectionnez un département"
                allowClear
                size="large"
                showSearch
                optionFilterProp="children"
              >
                {DEPARTMENT_OPTIONS.map(dept => (
                  <Option key={dept} value={dept}>
                    {dept}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="position"
              label="Poste"
            >
              <Input
                prefix={<TeamOutlined />}
                placeholder="Ex: Responsable des crédits"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="salary"
              label="Salaire (optionnel)"
            >
              <InputNumber
                prefix={<BankOutlined />}
                placeholder="Montant du salaire"
                size="large"
                style={{ width: '100%' }}
                formatter={(value) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' CFA' : ''}
                parser={(value: any) => value ? parseFloat(value.replace(/\s?CFA|(,*)/g, '')) : 0}
                min={0}
                step={1000}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label="Notes (optionnel)"
        >
          <TextArea
            placeholder="Informations complémentaires sur ce membre du personnel..."
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default StaffCreateModal;